/**
 * Everything the Desktop surface decides, with no DOM.
 *
 * The panel streams the runtime's whole X display and — once the user takes
 * control — turns their pointer and keyboard into `ComputerCommand`s against
 * that same display. Both halves have exactly one rule that matters and both
 * are easy to get subtly wrong, so both live here where they can be asserted
 * without mounting anything:
 *
 * - **Coordinates.** The video is letterboxed (`object-contain`) inside a panel
 *   whose aspect ratio rarely matches the display's. Mapping a click with the
 *   element's box instead of the *painted* box lands it tens of pixels away —
 *   far enough to hit the wrong window, close enough to look like lag.
 * - **Control.** Watching and controlling must be distinguishable at a glance.
 *   An agent is working in this display; a stray click from a user who believes
 *   they are only watching is a real edit to a real machine.
 */
import type { ComputerCommand } from "@cmmd-forge/contracts";

export type DesktopPanelState =
  /** No runtime session exists yet, so there is no display to stream. */
  | "no-session"
  /** A session has been requested and the call has not come back yet. */
  | "starting"
  /** A session exists and the stream is being negotiated. */
  | "connecting"
  /** Stream is up; input is not being forwarded. */
  | "watching"
  /** Stream is up and the user's input is driving the display. */
  | "controlling";

export interface DesktopPanelStateInput {
  readonly hasSession: boolean;
  readonly streamReady: boolean;
  readonly controlHeld: boolean;
  /** A start request is in flight. Optional so existing callers are unchanged. */
  readonly starting?: boolean;
}

export function desktopPanelState(input: DesktopPanelStateInput): DesktopPanelState {
  if (!input.hasSession) return input.starting === true ? "starting" : "no-session";
  if (!input.streamReady) return "connecting";
  return input.controlHeld ? "controlling" : "watching";
}

/**
 * The Desktop surface can start its own display.
 *
 * It could not, until now: the copy sent the user to the Browser surface,
 * because the desktop IS the display a browser session creates and a second
 * opener was assumed to race it. That assumption does not hold. The server's
 * `openSession` is keyed on the THREAD and resolves an existing in-memory or
 * persisted session before creating one — "One browser session per thread. The
 * thread is the canonical key" (browser/Layers/Manager.ts) — and it warns
 * rather than forking when a second caller arrives with its own browserId. So
 * both surfaces can call the same seam and the seam dedupes.
 *
 * Offered only from `no-session`: once a session exists there is nothing to
 * start, and `starting` is excluded so a second click cannot issue a duplicate
 * request while the first is still in flight.
 */
export function canStartDesktop(state: DesktopPanelState): boolean {
  return state === "no-session";
}

/**
 * Control is offered only over a stream that is actually up.
 *
 * Handing someone a "Take control" button while the picture is still
 * negotiating invites input aimed at a frame that is not there yet.
 */
export function canTakeDesktopControl(state: DesktopPanelState): boolean {
  return state === "watching" || state === "controlling";
}

/**
 * The status bar is for a live stream, not a narrator.
 *
 * While the display negotiated, the panel showed a header reading "Connecting to
 * the desktop" directly above an empty body that already said exactly that —
 * two lines of chrome for one fact, and no control to press, because control is
 * not offered until the stream is up. So the bar appears for precisely the
 * states that can take control: it never shows without its button, and the
 * button never shows without it. Everything earlier belongs in the body, where
 * the user is already looking at the empty space.
 */
export function showsDesktopStatusBar(state: DesktopPanelState): boolean {
  return canTakeDesktopControl(state);
}

export interface DesktopPanelCopy {
  readonly title: string;
  readonly detail: string;
}

export function desktopPanelCopy(state: DesktopPanelState): DesktopPanelCopy {
  switch (state) {
    case "no-session":
      return {
        title: "Desktop is off",
        detail: "Start it to view and control this Environment.",
      };
    case "starting":
      return {
        title: "Starting desktop",
        detail: "Preparing the Environment display.",
      };
    case "connecting":
      return { title: "Connecting display", detail: "Waiting for the first video frame." };
    case "watching":
      return { title: "Watching", detail: "Take control to use the mouse and keyboard yourself." };
    case "controlling":
      return { title: "You have control", detail: "Your mouse and keyboard drive this display." };
  }
}

export interface PaintedVideoBox {
  /** The element's box, from `getBoundingClientRect()`. */
  readonly elementWidth: number;
  readonly elementHeight: number;
  /** The stream's intrinsic size — `videoWidth` / `videoHeight`, not CSS pixels. */
  readonly videoWidth: number;
  readonly videoHeight: number;
}

export interface PaintedVideoRect {
  readonly offsetX: number;
  readonly offsetY: number;
  readonly width: number;
  readonly height: number;
}

/**
 * Where `object-contain` actually paints the frame inside the element.
 *
 * Returns null when any dimension is zero — before metadata loads, `videoWidth`
 * is 0 and every derived coordinate would be `NaN` or `Infinity`.
 */
export function paintedVideoRect(box: PaintedVideoBox): PaintedVideoRect | null {
  if (
    box.elementWidth <= 0 ||
    box.elementHeight <= 0 ||
    box.videoWidth <= 0 ||
    box.videoHeight <= 0
  ) {
    return null;
  }
  const scale = Math.min(box.elementWidth / box.videoWidth, box.elementHeight / box.videoHeight);
  const width = box.videoWidth * scale;
  const height = box.videoHeight * scale;
  return {
    offsetX: (box.elementWidth - width) / 2,
    offsetY: (box.elementHeight - height) / 2,
    width,
    height,
  };
}

export interface PointerMapInput extends PaintedVideoBox {
  /** Pointer position relative to the element's top-left corner. */
  readonly pointerX: number;
  readonly pointerY: number;
}

export interface DisplayPoint {
  readonly x: number;
  readonly y: number;
}

/**
 * Element-relative pointer position to a pixel on the runtime display.
 *
 * Returns null for a pointer in the letterbox bars: there is no display pixel
 * under it, and clamping to the nearest edge would silently move the click.
 *
 * Coordinates come back as non-negative integers because that is what the
 * contract's `Coordinate` accepts — a fractional or negative value is a schema
 * rejection at the RPC boundary, not a slightly-off click.
 */
export function mapPointerToDisplay(input: PointerMapInput): DisplayPoint | null {
  const rect = paintedVideoRect(input);
  if (rect === null) return null;
  const withinX = input.pointerX - rect.offsetX;
  const withinY = input.pointerY - rect.offsetY;
  if (withinX < 0 || withinY < 0 || withinX > rect.width || withinY > rect.height) {
    return null;
  }
  const x = Math.round((withinX / rect.width) * input.videoWidth);
  const y = Math.round((withinY / rect.height) * input.videoHeight);
  return {
    x: Math.min(Math.max(x, 0), input.videoWidth - 1),
    y: Math.min(Math.max(y, 0), input.videoHeight - 1),
  };
}

const MOUSE_BUTTON_BY_INDEX: Readonly<Record<number, "left" | "middle" | "right">> = {
  0: "left",
  1: "middle",
  2: "right",
};

export function mouseButtonFromEventButton(button: number): "left" | "middle" | "right" | null {
  return MOUSE_BUTTON_BY_INDEX[button] ?? null;
}

/**
 * X keysym names for the DOM keys whose `event.key` is not already one.
 *
 * Only the keys that differ are listed. A printable character's `event.key` is
 * its own keysym (`a`, `A`, `7`), so passing it through unchanged is correct —
 * and a table that restated them would be one more place to drift.
 */
const KEYSYM_BY_DOM_KEY: Readonly<Record<string, string>> = {
  " ": "space",
  ArrowDown: "Down",
  ArrowLeft: "Left",
  ArrowRight: "Right",
  ArrowUp: "Up",
  Backspace: "BackSpace",
  Delete: "Delete",
  End: "End",
  Enter: "Return",
  Escape: "Escape",
  Home: "Home",
  Insert: "Insert",
  PageDown: "Next",
  PageUp: "Prior",
  Tab: "Tab",
};

/** Keys that only ever modify another key; sending one alone is meaningless. */
const MODIFIER_KEYS: ReadonlySet<string> = new Set([
  "Alt",
  "AltGraph",
  "CapsLock",
  "Control",
  "Meta",
  "NumLock",
  "OS",
  "Shift",
]);

export interface KeyEventShape {
  readonly key: string;
  readonly altKey: boolean;
  readonly ctrlKey: boolean;
  readonly metaKey: boolean;
  readonly shiftKey: boolean;
}

/**
 * A DOM key event as an `xdotool key` combo, or null when there is nothing to
 * send (a bare modifier, or a key with no keysym).
 *
 * `shift` is deliberately omitted for printable characters: the browser has
 * already applied it, so `event.key` is `A`, and sending `shift+A` would ask X
 * for shift twice and can produce the wrong glyph on non-US layouts.
 */
export function keyComboFromEvent(event: KeyEventShape): string | null {
  if (MODIFIER_KEYS.has(event.key)) return null;
  const keysym = KEYSYM_BY_DOM_KEY[event.key] ?? (event.key.length === 1 ? event.key : null);
  if (keysym === null) return null;
  const printable = event.key.length === 1;
  const modifiers = [
    ...(event.ctrlKey ? ["ctrl"] : []),
    ...(event.altKey ? ["alt"] : []),
    ...(event.metaKey ? ["super"] : []),
    ...(event.shiftKey && !printable ? ["shift"] : []),
  ];
  return [...modifiers, keysym].join("+");
}

/**
 * A key event as the command to send.
 *
 * An unmodified printable character goes through `type`, not `key`, because
 * `xdotool key` takes X keysym *names*: `a` works, but `!` is `exclam` and `#`
 * is `numbersign`. `type` takes the literal character on any layout, so routing
 * printables there removes a whole symbol table — and the class of bug where a
 * user's `?` silently arrives as nothing.
 *
 * Anything with a modifier is a shortcut rather than text, so it goes through
 * `key`, where `ctrl+c` means what the user meant.
 */
export function commandForKeyEvent(event: KeyEventShape): ComputerCommand | null {
  if (MODIFIER_KEYS.has(event.key)) return null;
  const modified = event.ctrlKey || event.altKey || event.metaKey;
  if (!modified && event.key.length === 1) {
    return { kind: "type", text: event.key };
  }
  const combo = keyComboFromEvent(event);
  return combo === null ? null : { kind: "key", combo };
}

/**
 * Below this many display pixels, a press-and-release is a click, not a drag.
 *
 * Without a threshold every click on a trackpad becomes a one-pixel drag, which
 * X delivers as a selection gesture rather than an activation — the button
 * highlights and never fires.
 */
const DRAG_THRESHOLD_PX = 4;

/**
 * The command for a completed press-and-release.
 *
 * Both cases are decided here rather than at two call sites so the threshold
 * cannot be applied to one and not the other.
 */
export function commandForPointerRelease(
  from: DisplayPoint,
  to: DisplayPoint,
  button: "left" | "middle" | "right",
): ComputerCommand {
  const moved = Math.hypot(to.x - from.x, to.y - from.y);
  if (moved < DRAG_THRESHOLD_PX) {
    return { kind: "click", x: to.x, y: to.y, button };
  }
  return { kind: "drag", fromX: from.x, fromY: from.y, toX: to.x, toY: to.y, button };
}

/**
 * Wheel deltas to a scroll command.
 *
 * Deltas are rounded because the contract's `Delta` is an integer, and a
 * trackpad emits fractional ones constantly. A delta that rounds to zero on
 * both axes yields null rather than a command that would do nothing — inertial
 * scrolling tails off in exactly those events, and forwarding them would be a
 * burst of RPCs with no effect on screen.
 */
export function scrollCommandFor(
  point: DisplayPoint,
  deltaX: number,
  deltaY: number,
): Extract<ComputerCommand, { kind: "scroll" }> | null {
  const roundedX = Math.round(deltaX);
  const roundedY = Math.round(deltaY);
  if (roundedX === 0 && roundedY === 0) return null;
  return { kind: "scroll", x: point.x, y: point.y, deltaX: roundedX, deltaY: roundedY };
}
